// framework/client/core/PlatformManager.js
import { ComponentBase } from "./ComponentBase.js";

/** 
 * Abstract base class for platform-specific abstractions.
 * Defines the contract for interacting with platform features like lifecycle,
 * network status, and permissions in a unified way.
 * This class should not be instantiated directly.
 */
export class PlatformManager extends ComponentBase {
    constructor(config) {
        super(config);
        this.platformName = 'base';
        this.isOnline = true;
        this.isForeground = true;
    }

    async initialize() {
        this.log.info(`Initializing PlatformManager for: ${this.platformName}`);
        await super.initialize();
    }

    /**
     * Returns the name of the current platform.
     * @returns {string} 'pwa', 'electron', or 'capacitor'
     */
    getPlatformName() {
        return this.platformName;
    }

    /**
     * Returns a snapshot of the current platform state.
     * @returns {{name: string, isOnline: boolean, isForeground: boolean}}
     */
    getPlatformInfo() {
        return {
            name: this.platformName,
            isOnline: this.isOnline,
            isForeground: this.isForeground,
        };
    }

    /**
     * Checks the current status of a given permission without prompting the user.
     * @param {string} permissionType - e.g., 'notifications', 'microphone'
     * @returns {Promise<{status: 'granted' | 'denied' | 'prompt'}>}
     */
    async checkPermission(permissionType) {
        throw new Error(`checkPermission() must be implemented by subclass for type: ${permissionType}`);
    }

    /**
     * Requests a permission from the user.
     * @param {string} permissionType - e.g., 'notifications', 'microphone'
     * @returns {Promise<{status: 'granted' | 'denied'}>}
     */
    async requestPermission(permissionType) {
        throw new Error(`requestPermission() must be implemented by subclass for type: ${permissionType}`);
    }

    /**
     * Sets the badge number on the application icon.
     * @param {number} count - The number to display on the badge.
     */
    async setAppBadge(count) {
        this.log.debug(`setAppBadge(${count}) called on base PlatformManager. No action taken.`);
    }

    /**
     * Clears the badge from the application icon.
     */
    async clearAppBadge() {
        this.log.debug(`clearAppBadge() called on base PlatformManager. No action taken.`);
    }

    /**
     * Triggers the installation of a pending application update.
     */
    async applyUpdate() {
        this.log.debug(`applyUpdate() called on base PlatformManager. No action taken.`);
    }

    /**
     * Opens an external URL in the system browser/new window.
     * @param {string} url - The URL to open.
     * @param {object} [options] - Options for link opening.
     * @param {boolean} [options.showConfirmOverlay] - Whether to show a confirmation dialog.
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async openExternalUrl(url, options = {}) {
        throw new Error(`openExternalUrl() must be implemented by subclass for URL: ${url}`);
    }

    destroy() {
        this.log.info(`Destroying PlatformManager for: ${this.platformName}`);
        super.destroy();
    }
} 